import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gem, Sparkles, TrendingUp, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, signupStart, signupVerify, resendOtp } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | otp
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [form, setForm] = useState({ identifier: "", email: "", phone: "", password: "", name: "", business_name: "" });
  const [otp, setOtp] = useState("");
  const [otpEmail, setOtpEmail] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const cleanErr = (err) => {
    const d = err.response?.data?.detail;
    let msg = Array.isArray(d) ? (d[0]?.msg || "Please check your details") : (d || "Something went wrong");
    return String(msg).replace(/^Value error,\s*/i, "");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.identifier, form.password);
        navigate(u?.role === "founder" ? "/founder" : "/dashboard");
      } else {
        const res = await signupStart({
          name: form.name, email: form.email, phone: form.phone || null,
          password: form.password, business_name: form.business_name || `${form.name}'s Business`,
          industries: ["restaurant"],
        });
        setOtpEmail(res.email);
        setMode("otp");
        setOtp("");
        toast.success(res.message || "Request created. Ask your admin for the code.");
      }
    } catch (err) {
      toast.error(cleanErr(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.trim().length !== 9) return toast.error("Enter the 9-character code");
    setLoading(true);
    try {
      await signupVerify(otpEmail, otp.trim());
      toast.success("Verified — welcome to KHEVORA OS!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      const res = await resendOtp(otpEmail);
      toast.success(res.message || "A new code was generated. Ask your admin for it.");
    } catch (err) {
      toast.error(cleanErr(err));
    } finally {
      setResending(false);
    }
  };

  const demoLogin = async () => {
    setLoading(true);
    try {
      try {
        await login("owner@khevora.com", "khevora123");
      } catch {
        // demo data may not be present yet — seed it, then retry once
        await api.post("/seed");
        await login("owner@khevora.com", "khevora123");
      }
      navigate("/dashboard");
    } catch {
      toast.error("Demo unavailable right now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cover bg-center" style={{ backgroundImage: "url(/bg-login.jpg)" }}>
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden bg-cover bg-center" style={{ backgroundImage: "url(/bg-login.jpg)" }}>
        <div className="absolute inset-0 bg-[#052e22]/70" />
        <div className="absolute inset-0 opacity-[0.18]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #E6B94E 0, transparent 42%), radial-gradient(circle at 80% 72%, #0f6b4d 0, transparent 45%)" }} />
        <div className="relative flex items-center gap-3">
          <div className="grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br from-amber-300 to-yellow-600 text-neutral-900">
            <Gem className="h-6 w-6" />
          </div>
          <div>
            <div className="font-heading text-xl font-bold tracking-tight">KHEVORA OS</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">Restaurant Operating System</div>
          </div>
        </div>
        <div className="relative space-y-8 max-w-md">
          <h2 className="font-heading text-4xl font-bold leading-tight tracking-tight">
            Run your restaurant<br />
            <span className="text-amber-400">like a well-run kitchen.</span>
          </h2>
          <div className="space-y-5">
            {[
              { icon: Sparkles, t: "AI Business Advisor", d: "Ask anything about your restaurant in plain language." },
              { icon: TrendingUp, t: "Tables, Orders & KOT", d: "Dine-in, takeaway, live kitchen tickets & menu, in one place." },
              { icon: ShieldCheck, t: "Built for India", d: "GST-ready bills, ₹ lakh/crore, WhatsApp invoices to guests." },
            ].map((f) => (
              <div key={f.t} className="flex gap-4">
                <div className="grid place-items-center h-10 w-10 rounded-lg bg-white/10 shrink-0">
                  <f.icon className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <div className="font-semibold">{f.t}</div>
                  <div className="text-sm text-white/60">{f.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/40">Trusted by serious businesses across India.</div>
      </div>

      {/* Right form */}
      <div className="relative flex items-center justify-center p-6 md:p-12 bg-background/85 backdrop-blur-xl border-l border-white/5">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-gradient-to-br from-amber-300 to-yellow-600 text-neutral-900">
              <Gem className="h-5 w-5" />
            </div>
            <div className="font-heading text-lg font-bold">KHEVORA OS</div>
          </div>

          <h1 className="font-heading text-2xl font-bold tracking-tight">
            {mode === "login" ? "Welcome back" : mode === "otp" ? "Verify your account" : "Create your account"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 mb-6">
            {mode === "login" ? "Sign in to your business command center."
              : mode === "otp" ? <>Enter the verification code for <span className="font-semibold text-foreground">{otpEmail}</span> given by your admin.</>
              : "Start managing your business smarter."}
          </p>

          {mode === "otp" ? (
            <>
              <div className="mb-4 rounded-lg border border-amber-400/30 bg-amber-400/10 p-3 text-sm">
                <span className="font-semibold text-amber-400">Ask the KHEVORA admin</span> for your verification code, then enter it below to finish creating your account.
              </div>
              <form onSubmit={verifyOtp} className="space-y-4">
                <div>
                  <Label htmlFor="otp">Verification code</Label>
                  <Input
                    id="otp" data-testid="otp-input" maxLength={9}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9))}
                    autoFocus required className="mt-1.5 text-center text-xl tracking-[0.3em] font-bold uppercase"
                    placeholder="XXXXXXXXX"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">9-character code (letters &amp; numbers) provided by your admin.</p>
                </div>
                <Button type="submit" data-testid="verify-otp-btn" disabled={loading} className="w-full font-semibold bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-neutral-900">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Continue"}
                </Button>
              </form>
              <div className="flex items-center justify-between mt-4 text-sm">
                <button data-testid="resend-otp-btn" onClick={resend} disabled={resending} className="text-amber-400 font-semibold hover:underline disabled:opacity-50">
                  {resending ? "Requesting…" : "Request new code"}
                </button>
                <button data-testid="back-to-signup" onClick={() => setMode("register")} className="text-muted-foreground hover:text-foreground">
                  Change details
                </button>
              </div>
            </>
          ) : (
          <>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label htmlFor="name">Your name</Label>
                  <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required className="mt-1.5" placeholder="Aarav Khanna" />
                </div>
                <div>
                  <Label htmlFor="biz">Business name</Label>
                  <Input id="biz" data-testid="register-business" value={form.business_name} onChange={set("business_name")} className="mt-1.5" placeholder="Khevora Emporium" />
                </div>
              </>
            )}
            {mode === "login" ? (
              <div>
                <Label htmlFor="identifier">Email or phone</Label>
                <Input id="identifier" data-testid="login-email" value={form.identifier} onChange={set("identifier")} required className="mt-1.5" placeholder="you@business.com or +91…" />
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" placeholder="you@business.com" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Input id="phone" data-testid="register-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" placeholder="+91 98765 43210" />
                </div>
              </>
            )}
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={form.password} onChange={set("password")} required className="mt-1.5" placeholder="••••••••" />
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full font-semibold bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-neutral-900">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Sign in" : "Send verification code"}
            </Button>
          </form>

          <div className="relative my-5 text-center">
            <span className="relative z-10 bg-background px-3 text-xs uppercase tracking-wider text-muted-foreground">or</span>
            <div className="absolute inset-y-1/2 left-0 right-0 h-px bg-border" />
          </div>

          <div className="space-y-2.5">
            <Button variant="outline" onClick={googleLogin} data-testid="google-login" className="w-full font-medium">
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continue with Google
            </Button>
            <Button variant="secondary" onClick={demoLogin} data-testid="demo-login" className="w-full font-medium">
              <Sparkles className="h-4 w-4 mr-2 text-amber-400" /> Explore live demo
            </Button>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === "login" ? "New to KHEVORA?" : "Already have an account?"}{" "}
            <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-semibold text-amber-400 hover:underline">
              {mode === "login" ? "Create account" : "Sign in"}
            </button>
          </p>
          </>
          )}

          <div className="mt-10 text-center" data-testid="founder-credit">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              <span className="h-px w-6 bg-border" /> Founded by <span className="h-px w-6 bg-border" />
            </div>
            <div className="mt-1.5 font-heading text-lg font-extrabold tracking-tight bg-gradient-to-r from-amber-300 via-yellow-500 to-amber-300 bg-clip-text text-transparent">
              Rohit &amp; Sohan
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
